import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { LANGUAGES, dirFor } from "@/i18n";
import { SITE } from "@/config";

export default function Seo() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage || i18n.language || "en";
  const origin = typeof window !== "undefined" ? window.location.origin : SITE.url;
  const canonical = `${origin}/${lng}`;
  const title = t("seo.title");
  const description = t("seo.description");
  const dir = dirFor(lng);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "@id": `${origin}/#person`,
        name: SITE.name,
        url: origin,
        jobTitle: "Entrepreneur & Builder",
        description,
        sameAs: [SITE.instagram, SITE.youtube],
        address: {
          "@type": "PostalAddress",
          addressLocality: "Gurugram",
          addressCountry: "IN",
        },
      },
      {
        "@type": "Organization",
        "@id": `${origin}/#organization`,
        name: SITE.name,
        url: origin,
        founder: { "@id": `${origin}/#person` },
      },
      {
        "@type": "ProfessionalService",
        "@id": `${origin}/#service`,
        name: `${SITE.name} — Consulting`,
        url: origin,
        description,
        provider: { "@id": `${origin}/#person` },
        areaServed: "Worldwide",
        serviceType: ["Real Estate", "Digital Marketing", "AI Automation", "E-commerce"],
      },
      {
        "@type": "WebSite",
        "@id": `${origin}/#website`,
        url: origin,
        name: SITE.name,
        inLanguage: lng,
        publisher: { "@id": `${origin}/#organization` },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("nav.home"), item: canonical },
        ],
      },
    ],
  };

  return (
    <Helmet htmlAttributes={{ lang: lng, dir }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={canonical} />

      {LANGUAGES.map((l) => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={`${origin}/${l.code}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${origin}/en`} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={lng} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}
